import { CIRouteguard, useUserPermission } from 'ccs-ui-components';
import { useEffect } from 'react';
import { Navigate, useMatch } from 'react-router-dom';

import { Provider } from 'react-redux';
import { requiredAuthPermission } from '../../../common/constants/UserPermissionConstants';
import { RouteObject } from '../../../common/types/interfaces';
import { journeyBuilderRoutes } from '../../constants/JdRoutes';
import { store } from '../../store/AppStore';
import CreateJourney from '../create-journeys/CreateJourney';
import ImportJourney from '../import-journeys/ImportJourney';
import JourneyCanvas from '../journey-canvas/JourneyCanvas';
import JourneyList from '../journey-list/JourneyList';

interface MyWindow extends Window {
  showHeader?: () => void;
  hideHeader?: () => void;
}
interface JourneyBuilderWrapperProps {
  showHeaderHandler: (value: boolean) => void;
}

export const JourneyBuilderWrapper = ({ showHeaderHandler }: JourneyBuilderWrapperProps) => {
  const { permissionObject } = useUserPermission();

  // check if the route matches journey canvas (either the journey-level or step-level URL)
  const canvasMatch = useMatch(journeyBuilderRoutes.journeyCanvas);
  const canvasStepMatch = useMatch(journeyBuilderRoutes.journeyCanvasStep);
  const isCanvasPage = canvasMatch?.pattern.path === journeyBuilderRoutes.journeyCanvas
    || canvasStepMatch?.pattern.path === journeyBuilderRoutes.journeyCanvasStep;

  // provision for micro-frontend to show/hide header
  useEffect(() => {
    const win = window as MyWindow;
    showHeaderHandler(true);
    if (isCanvasPage) {
      win.hideHeader?.call(null);
    } else {
      win.showHeader?.call(null);
    }
  }, [isCanvasPage]);

  const routes: RouteObject[] = [
    {
      path: '/',
      element: <Navigate replace to={journeyBuilderRoutes.viewJourney} />,
      requiredPermissions: [requiredAuthPermission.JD.view, requiredAuthPermission.JD.edit, requiredAuthPermission.JD.approve]
    },
    {
      path: journeyBuilderRoutes.viewJourney.substring(journeyBuilderRoutes.parentRoute.length),
      element: <JourneyList />,
      requiredPermissions: [requiredAuthPermission.JD.view, requiredAuthPermission.JD.edit, requiredAuthPermission.JD.approve]
    },
    {
      path: journeyBuilderRoutes.createJourney.substring(journeyBuilderRoutes.parentRoute.length),
      element: <CreateJourney />,
      requiredPermissions: [requiredAuthPermission.JD.edit, requiredAuthPermission.JD.approve]
    },
    {
      path: '/create-journey',
      element: <Navigate to={journeyBuilderRoutes.createJourney.substring(journeyBuilderRoutes.parentRoute.length)} replace />,
      requiredPermissions: [requiredAuthPermission.JD.edit, requiredAuthPermission.JD.approve]
    },
    {
      path: '/journeys/*',
      element: <ImportJourney />,
      requiredPermissions: []
    },
    {
      path: journeyBuilderRoutes.journeyCanvas.substring(journeyBuilderRoutes.parentRoute.length),
      element: <JourneyCanvas />,
      requiredPermissions: [requiredAuthPermission.JD.view, requiredAuthPermission.JD.edit, requiredAuthPermission.JD.approve]
    },
    {
      path: journeyBuilderRoutes.journeyCanvasStep.substring(journeyBuilderRoutes.parentRoute.length),
      element: <JourneyCanvas />,
      requiredPermissions: [requiredAuthPermission.JD.view, requiredAuthPermission.JD.edit, requiredAuthPermission.JD.approve]
    },
    {
      path: '*',
      element: <Navigate replace to={journeyBuilderRoutes.viewJourney} />,
      requiredPermissions: [requiredAuthPermission.JD.view, requiredAuthPermission.JD.edit, requiredAuthPermission.JD.approve]
    }
  ];

  return (
    <Provider store={store}>
      <CIRouteguard routes={routes} userPermissions={permissionObject.permission} />
    </Provider>
  );
};

export default JourneyBuilderWrapper;
